import { ReactComponent as BookingsIcon } from '@/assets/icons/bookings-black.svg';
import {
  BottomSheet,
  Button,
  Chip,
  HSpacer,
  IconButton,
  MenuItem,
  Search,
  SideSheet,
  Text,
  TopAppBar,
  VSpacer,
} from '@/components/DesignSystem';
import { FilterChip } from '@/components/DesignSystem/Toolbar/FilterChip';
import { FilterMenu } from '@/components/DesignSystem/Toolbar/FilterMenu';
import { FilterSelector } from '@/components/DesignSystem/Toolbar/FilterSelector';
import { FilterSheet } from '@/components/DesignSystem/Toolbar/FilterSheet';
import {
  Filter,
  FilterSelections,
  NonBooleanFilter,
} from '@/components/DesignSystem/Toolbar/interfaces';
import { DesktopOnly } from '@/components/shared/DesktopOnly';
import { MobileOnly } from '@/components/shared/MobileOnly';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSearch } from '@/hooks/useSearch';
import { defaultDashboardData } from '@/pages/CustomerDashboard/CustomerDashboard';
import { AboutAccountRolesBanner } from '@/pages/CustomerDashboard/LinkedAccounts';
import { SXStyles } from "@/themes/variant-interfaces/SXStyles";
import { DashboardApi } from '@/utilities/api/DashboardApi';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { getAvailableCustomers as getAvailableCustomersUtil } from '@/utilities/Farmer';
import { DashboardEndpoint } from '@api/endpoints/DashboardEndpoint';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  ButtonBase,
  CircularProgress,
  Container,
  ListItemText,
  Menu,
  Stack,
  useTheme,
} from '@mui/material';
import { CIBPageName, OrderTransactionStatus } from '@shared/enums';
import { getAccountName } from '@shared/utilities/UserUtilities';
import React, { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardNavigate } from '../helpers';
import { BookingListItem } from './BookingListItem';
import { PointsRangeFilterModal } from './PointsRangeFilterModal';
import { PointsRangeFilter } from './types';

const PAGE_SIZE = 20;

export const BookingsPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();
  const { data: dashboardData } = useDashboard(accessToken);
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => {
    const defaultSelections = new Map();
    defaultSelections.set('status', new Set([OrderTransactionStatus.ACTIVE]));
    return defaultSelections;
  });
  const { search, setSearch, debouncedSearch } = useSearch(1);
  const [onlyWithPointsActivity, setOnlyWithPointsActivity] = useState<boolean>(false);
  const [showCustomerFilterSheet, setShowCustomerFilterSheet] = useState(false);
  const [customerFilterSelections, setCustomerFilterSelections] =
    useState<FilterSelections>(new Map());
  const [selectedStatusFilterId, setSelectedStatusFilterId] = useState<string | null>(null);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [showStatusFilterSheet, setShowStatusFilterSheet] = useState(false);
  const [showPointsRangeFilterModal, setShowPointsRangeFilterModal] = useState(false);
  const [bookingsListAnchorEl, setBookingsListAnchorEl] = useState<null | HTMLElement>(null);
  const [pointsRangeFilter, setPointsRangeFilter] = useState<PointsRangeFilter>({});

  const { farmer, linkedAccounts } = dashboardData ?? defaultDashboardData;

  const { data: latestProcessedOrderDate } = useQuery(
    [QueryKeys.GET_LATEST_PROCESSED_ORDER_DATE, dashboardData?.retailer.id],
    () => OrderTransactionsApi.getLatestProcessedOrderDate({
      accessToken,
      retailerId: dashboardData?.retailer.id,
    }),
    { enabled: !!dashboardData?.retailer.id },
  );

  const styles: SXStyles = {
    dropdownIcon: {
      height: '18px',
      marginRight: '-4px',
      width: '18px',
    },
  } as const;

  const isQueryEnabled = !!accessToken && typeof accessToken === 'string' && accessToken.length > 0;

  const queryParams = useMemo(() => {
    const params: DashboardEndpoint.ListBookings.Query = {
      limit: PAGE_SIZE,
    };

    if (onlyWithPointsActivity) {
      params.onlyWithPointsActivity = true;
    }

    const selectedCustomerIds = Array.from(filterSelections?.get('customer') ?? []);
    if (selectedCustomerIds.length > 0) {
      params.farmerIds = selectedCustomerIds;
    }

    const statusSelection = filterSelections?.get('status');
    params.statuses = statusSelection && statusSelection.size > 0
      ? Array.from(statusSelection) as DashboardEndpoint.ListBookings.BookingStatusFilter[]
      : [OrderTransactionStatus.ACTIVE];

    if (pointsRangeFilter.remaining) {
      params.pointsRemaining = pointsRangeFilter.remaining;
    }
    if (pointsRangeFilter.redeemed) {
      params.pointsRedeemed = pointsRangeFilter.redeemed;
    }
    if (pointsRangeFilter.reserved) {
      params.pointsReserved = pointsRangeFilter.reserved;
    }

    if (debouncedSearch?.trim()) {
      params.search = debouncedSearch.trim();
    }
    return params;
  }, [onlyWithPointsActivity, filterSelections, pointsRangeFilter, debouncedSearch]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.LIST_BOOKINGS, accessToken, queryParams],
    ({ pageParam = 0 }) => {
      return DashboardApi.listBookings(accessToken!, {
        ...queryParams,
        page: pageParam,
      });
    },
    {
      enabled: isQueryEnabled,
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page
          ? lastPage.page + 1
          : undefined
      ),
    },
  );


  const allBookings = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) ?? [];
  }, [data]);

  const totalCount = data?.pages?.[0]?.total ?? 0;

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (scrollBottom < 100 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const brandColor = dashboardData?.retailer.brandColor ?? theme.palette.primary.main;
  const hasLinkedAccounts = dashboardData?.linkedAccounts
    && dashboardData.linkedAccounts.length > 0;

  const availableCustomers = useMemo(() => {
    return getAvailableCustomersUtil(dashboardData, { maxLabelLength: 30 });
  }, [dashboardData]);

  const customerFilter: Filter | null = useMemo(() => {
    if (hasLinkedAccounts && availableCustomers.length > 1) {
      return {
        id: 'customer',
        label: t($ => $.common.customer),
        options: availableCustomers,
        selectionMethod: 'multi-select',
        truncateLabel: true,
      };
    }
    return null;
  }, [hasLinkedAccounts, availableCustomers, t]);

  const statusFilter: Filter = useMemo(() => ({
    id: 'status',
    label: t($ => $.bookings.filters.status),
    options: [
      { id: OrderTransactionStatus.ACTIVE, label: t($ => $.common.status.active), default: true },
      { id: OrderTransactionStatus.EXPIRED, label: t($ => $.common.status.expired) },
      { id: OrderTransactionStatus.CLOSED, label: t($ => $.common.status.closed) },
    ],
    selectionMethod: 'multi-select',
  }), [t]);

  const handleFilterChange =
    useCallback((result: { search?: string; selections?: FilterSelections }) => {
      setSearch(result.search ?? '');
      if (result.selections) {
        const statusSelection = result.selections.get('status');
        if (statusSelection && statusSelection.size === 0) {
          const newSelections = new Map(result.selections);
          newSelections.set('status', new Set([OrderTransactionStatus.ACTIVE]));
          setFilterSelections(newSelections);
        } else {
          setFilterSelections(result.selections);
        }
      } else {
        setFilterSelections(new Map());
      }
    }, [setSearch]);

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems="center">
          <VSpacer size="14" />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  const Header = (
    <TopAppBar
      isStatic
      leftAccessory={(
        <IconButton
          onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken)}
          sx={{ color: theme.palette.common.white }}
          testID="back-button"
        >
          <ArrowBack />
        </IconButton>
      )}
      rightAccessory={<HSpacer size="9" />}
      sx={{ bgcolor: brandColor }}
      testID="top-app-bar"
    >
      <Text category="title-large" sx={{ color: theme.palette.common.white }}>
        {t($ => $.bookings.title)}
      </Text>
    </TopAppBar>
  );

  if (!dashboardData?.retailer.hasBookings) {
    navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken);
    return null;
  }
  
  return (
    <>
      <Stack
        bgcolor={theme.palette.background.default}
        flexGrow={1}
        height="auto"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
      >
        {Header}
        <Stack bgcolor={theme.palette.background.default} flexGrow={1}>
          {!!latestProcessedOrderDate && (
            <>
              <VSpacer size="4" />
              <Stack alignItems="center">
                <Text category="body-xsmall">
                  {t($ => $.common.updated, { date: latestProcessedOrderDate })}
                </Text>
              </Stack>
            </>
          )}
          <VSpacer size="4" />
          <Stack px="16px">
            {!!farmer?.accountGroupRole && !!linkedAccounts?.length && (
              <>
                <AboutAccountRolesBanner
                  brandColor={brandColor}
                  customerRole={farmer.accountGroupRole}
                  linkedAccounts={linkedAccounts}
                  pageName={CIBPageName.CRD_BookingPage}
                  showMobileVersion={true}
                />
                <VSpacer size="4" />
              </>
            )}
            <ButtonBase
              data-testid="select-a-list-button"
              onClick={(e) => setBookingsListAnchorEl(e.currentTarget as HTMLElement)}
              sx={{
                borderRadius: '8px',
                width: '100%',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Stack
                alignItems="flex-start"
                direction="row"
                justifyContent="flex-start"
                sx={{
                  width: '100%',
                  p: '7px 8px',
                }}
              >
                <Stack direction="row" spacing={0}>
                  <Text
                    category="label-medium"
                    sx={{ color: brandColor }}
                  >
                    {t($ => $.bookings.filters.viewing)}
                  </Text>
                  <HSpacer size="2" />
                  <Text
                    category="label-medium"
                    sx={{ color: brandColor, fontWeight: theme.typography.fontWeightBold }}
                  >
                    {onlyWithPointsActivity
                      ? t($ => $.bookings.filters.onlyPointsActivity)
                      : t($ => $.bookings.filters.allBookings)}
                  </Text>
                </Stack>
                <HSpacer size="3"/>
                <KeyboardArrowDownIcon sx={{ height: '16px', width: '16px', color: brandColor }} />
              </Stack>
            </ButtonBase>
            <Menu
              anchorEl={bookingsListAnchorEl}
              onClose={() => setBookingsListAnchorEl(null)}
              open={Boolean(bookingsListAnchorEl)}
            >
              <MenuItem
                onClick={() => {
                  setOnlyWithPointsActivity(false);
                  setBookingsListAnchorEl(null);
                }}
                selected={!onlyWithPointsActivity}
                testID="view-all-bookings-menu-item"
              >
                <ListItemText>
                  <Stack direction="row" spacing={0}>
                    <Text category="body-medium" sx={{ color: brandColor }}>
                      {t($ => $.bookings.filters.viewing)}
                    </Text>
                    <HSpacer size="2" />
                    <Text category="body-medium" sx={{ color: brandColor }}>
                      {t($ => $.bookings.filters.allBookings)}
                    </Text>
                  </Stack>
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOnlyWithPointsActivity(true);
                  setBookingsListAnchorEl(null);
                }}
                selected={onlyWithPointsActivity}
                testID="view-points-activity-bookings-menu-item"
              >
                <ListItemText>
                  <Stack direction="row" spacing={0}>
                    <Text category="body-medium" sx={{ color: brandColor }}>
                      {t($ => $.bookings.filters.viewing)}
                    </Text>
                    <HSpacer size="2" />
                    <Text category="body-medium" sx={{ color: brandColor }}>
                      {t($ => $.bookings.filters.onlyPointsActivity)}
                    </Text>
                  </Stack>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
          <VSpacer size="4" />
          <Stack px="16px">
            <Search
              fullWidth
              onChangeText={setSearch}
              placeholder={t($ => $.common.search.searchById)}
              testID="bookings-search"
              value={search}
            />
            <VSpacer size="6" />
            <Stack
              direction="row"
              spacing={0}
              sx={{
                alignItems: 'center',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                scrollbarWidth: 'none',
              }}
            >
              {customerFilter && (
                <>
                  <Chip
                    label={
                      <Stack alignItems="center" direction="row">
                        <Text category="label-large">
                          {customerFilter.label}
                        </Text>
                        <>
                          <HSpacer size="3"/>
                          <ArrowDropDown sx={styles.dropdownIcon} />
                        </>
                      </Stack>
                    }
                    onClick={() => setShowCustomerFilterSheet(true)}
                    size="medium"
                    sx={{
                      borderColor: theme.palette.divider,
                      backgroundColor: (filterSelections.get('customer')?.size ?? 0) > 0
                        ? theme.palette.primaryContainer
                        : undefined,
                      '&:hover': {
                        backgroundColor: (filterSelections.get('customer')?.size ?? 0) > 0
                          ? theme.palette.primaryContainer
                          : undefined,
                      },
                      whiteSpace: 'nowrap',
                    }}
                    testID="customer-filter-chip"
                    variant={(filterSelections.get('customer')?.size ?? 0) > 0 ?
                      'filled' : 'outlined'}
                  />
                  <HSpacer size="3" />
                </>
              )}
              <FilterChip
                disabled={!statusFilter.options.length}
                filter={statusFilter}
                onClick={(event) => {
                  if (!isMobile) {
                    setSelectedStatusFilterId(statusFilter.id);
                    setStatusMenuAnchorEl(event.currentTarget);
                  } else {
                    setSelectedStatusFilterId(statusFilter.id);
                    setShowStatusFilterSheet(true);
                  }
                }}
                selection={filterSelections.get('status')}
                testID="status-filter-chip"
              />
              <HSpacer size="3" />
              <Chip
                label={
                  <Stack alignItems="center" direction="row">
                    <Text category="label-large">
                      {t($ => $.bookings.filters.pointsRange)}
                    </Text>
                      <>
                        <HSpacer size="3"/>
                        <ArrowDropDown sx={styles.dropdownIcon} />
                      </>
                  </Stack>
                }
                onClick={() => setShowPointsRangeFilterModal(true)}
                size="medium"

                sx={{
                  borderColor: theme.palette.divider,
                  backgroundColor:
                    (pointsRangeFilter.remaining || pointsRangeFilter.redeemed
                      || pointsRangeFilter.reserved)
                      ? theme.palette.primaryContainer
                      : undefined,
                  '&:hover': {
                    backgroundColor:
                      (pointsRangeFilter.remaining || pointsRangeFilter.redeemed
                        || pointsRangeFilter.reserved)
                        ? theme.palette.primaryContainer
                        : undefined,
                  },
                  whiteSpace: 'nowrap',
                }}
                testID="points-range-filter-chip"
                variant=
                  {(pointsRangeFilter.remaining ||
                    pointsRangeFilter.redeemed || pointsRangeFilter.reserved)
                    ? 'filled'
                    : 'outlined'}
              />
            </Stack>
            {!isMobile && selectedStatusFilterId === 'status' && (
              <FilterMenu
                anchorEl={statusMenuAnchorEl}
                filter={statusFilter as NonBooleanFilter}
                onChange={(selection) => {
                  const newSelections = new Map(filterSelections);
                  newSelections.set('status', selection);
                  handleFilterChange({ search: debouncedSearch, selections: newSelections });
                  if (statusFilter.selectionMethod === 'single-select') {
                    setSelectedStatusFilterId(null);
                  }
                }}
                onClose={() => setSelectedStatusFilterId(null)}
                open
                selection={filterSelections.get('status') ?? new Set()}
                testID="status-filter-menu"
                themeColor={brandColor}
              />
            )}
            <FilterSheet
              filters={[statusFilter]}
              onApply={(selections) => {
                const newSelections = new Map(filterSelections);
                newSelections.set('status', selections.get('status') ?? new Set());
                handleFilterChange({ search: debouncedSearch, selections: newSelections });
              }}
              onClose={() => {
                setShowStatusFilterSheet(false);
                setSelectedStatusFilterId(null);
              }}
              open={showStatusFilterSheet && selectedStatusFilterId === 'status'}
              selections={filterSelections}
              testID="status-filter-sheet"
              themeColor={brandColor}
              title={statusFilter.label}
            />
            <VSpacer size="4" />
            <Text category="body-medium" sx={{ color: theme.palette.grey[600] }}>
              {t($ => $.common.booking.count, { count: totalCount })}
            </Text>
          </Stack>
          <VSpacer size="4" />
          <Stack
            onScroll={handleScroll}
            sx={{
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
            }}
          >
            {isLoading ? (
              <Stack alignItems="center" py="48px">
                <CircularProgress />
              </Stack>
            ) : allBookings.length === 0 ? (
              <Stack alignItems="center" py="48px" spacing={1}>
                <BookingsIcon
                  style={{
                    width: '64px',
                    height: '64px',
                    color: brandColor,
                  }}
                />
                <Text category="title-large" textAlign="center">
                  {t($ => $.bookings.emptyState)}
                </Text>
              </Stack>
            ) : (
              allBookings.map((booking, index) => {
                const accountName = booking.farmer ? getAccountName(booking.farmer) : '';

                return (
                  <Stack
                    key={booking.id}
                    sx={{
                      borderTop: `1px solid rgba(0, 0, 0, 0.06)`,
                      borderBottom: index === allBookings.length - 1 ? `1px solid rgba(0, 0, 0, 0.06)` : 'none',
                    }}
                  >
                    <BookingListItem
                      accountName={accountName}
                      booking={booking}
                      key={booking.id}
                      showAccountName={!!hasLinkedAccounts}
                    />
                  </Stack>
                );
              })
            )}
            {hasNextPage && !isFetchingNextPage && (
              <Stack alignItems="center" py="16px">
                <Button
                  onClick={() => void fetchNextPage()}
                  testID="load-more-bookings"
                  variant="outlined"
                >
                  {t($ => $.common.actions.loadMore)}
                </Button>
              </Stack>
            )}
            {isFetchingNextPage && (
              <Stack alignItems="center" py="16px">
                <CircularProgress size={24} />
              </Stack>
            )}
          </Stack>
          <VSpacer size="8" />
        </Stack>
      </Stack>
      {customerFilter && (
        <>
          <DesktopOnly>
            <SideSheet
              actionButton={(props) => (
                <Button
                  {...props}
                  onClick={() => {
                    const newSelections = new Map(filterSelections);
                    newSelections.set('customer',
                      customerFilterSelections.get('customer') ?? new Set());
                    setFilterSelections(newSelections);
                    setShowCustomerFilterSheet(false);
                  }}
                  sx={{
                    backgroundColor: brandColor,
                    '&:hover': {
                      backgroundColor: brandColor,
                      opacity: 0.9,
                    },
                  }}
                  testID="customer-filter-apply-button"
                >
                  {t($ => $.common.actions.apply)}
                </Button>
              )}
              hideScrollbar
              onClose={() => {
                setShowCustomerFilterSheet(false);
                setCustomerFilterSelections(filterSelections);
              }}
              onOpen={() => setCustomerFilterSelections(new Map(filterSelections))}
              open={showCustomerFilterSheet}
              secondaryActionButton={(props) => (
                <Button
                  {...props}
                  onClick={() => {
                    const newSelections = new Map(filterSelections);
                    newSelections.set('customer', new Set());
                    setCustomerFilterSelections(newSelections);
                  }}
                  sx={{
                    borderColor: brandColor,
                    color: brandColor,
                    '&:hover': {
                      borderColor: brandColor,
                      color: brandColor,
                    },
                  }}
                  testID="customer-filter-clear-button"
                >
                  <Text sx={{ color: brandColor }}>{t($ => $.common.actions.clearAll)}</Text>
                </Button>
              )}
              testID="customer-filter-sheet"
              title={customerFilter.label}
            >
              <Stack px="24px" py="16px">
                <FilterSelector
                  filter={customerFilter}
                  onChange={(selection) => {
                    const newSelections = new Map(customerFilterSelections);
                    newSelections.set('customer', selection);
                    setCustomerFilterSelections(newSelections);
                  }}
                  selection={customerFilterSelections.get('customer') ?? new Set()}
                  testID="customer-filter-selector"
                  themeColor={brandColor}
                />
              </Stack>
            </SideSheet>
          </DesktopOnly>
          <MobileOnly>
            <BottomSheet
              footer={(
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ p: 2 }}
                >
                  <Button
                    fullWidth
                    onClick={() => {
                      const newSelections = new Map(filterSelections);
                      newSelections.set('customer',
                        customerFilterSelections.get('customer') ?? new Set());
                      setFilterSelections(newSelections);
                      setShowCustomerFilterSheet(false);
                    }}
                    sx={{
                      backgroundColor: brandColor,
                      '&:hover': {
                        backgroundColor: brandColor,
                        opacity: 0.9,
                      },
                    }}
                    testID="customer-filter-apply-button"
                    variant="contained"
                  >
                    <Text
                      category="label-large"
                      sx={{ color: theme.palette.common.white }}
                    >
                      {t($ => $.common.actions.apply)}
                    </Text>
                  </Button>
                  <Button
                    fullWidth
                    onClick={() => {
                      const newSelections = new Map(filterSelections);
                      newSelections.set('customer', new Set());
                      setCustomerFilterSelections(newSelections);
                    }}
                    sx={{
                      borderColor: brandColor,
                      color: brandColor,
                      '&:hover': {
                        borderColor: brandColor,
                        color: brandColor,
                      },
                    }}
                    testID="customer-filter-clear-button"
                    variant="outlined"
                  >
                    <Text category="label-large" sx={{ color: brandColor }}>
                      {t($ => $.common.actions.clearAll)}
                    </Text>
                  </Button>
                </Stack>
              )}
              onClose={() => {
                setShowCustomerFilterSheet(false);
                setCustomerFilterSelections(filterSelections);
              }}
              onOpen={() => setCustomerFilterSelections(new Map(filterSelections))}
              open={showCustomerFilterSheet}
              showPuller={true}
              testID="customer-filter-sheet"
              title={customerFilter.label}
            >
              <Stack px="24px" py="16px">
                <FilterSelector
                  filter={customerFilter}
                  onChange={(selection) => {
                    const newSelections = new Map(customerFilterSelections);
                    newSelections.set('customer', selection);
                    setCustomerFilterSelections(newSelections);
                  }}
                  selection={customerFilterSelections.get('customer') ?? new Set()}
                  testID="customer-filter-selector"
                  themeColor={brandColor}
                />
              </Stack>
            </BottomSheet>
          </MobileOnly>
        </>
      )}
      <PointsRangeFilterModal
        onApply={(filter) => {
          setPointsRangeFilter(filter);
        }}
        onClose={() => setShowPointsRangeFilterModal(false)}
        open={showPointsRangeFilterModal}
        themeColor={brandColor}
        value={pointsRangeFilter}
      />
    </>
  );
};

